import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import PageLoader from "@/components/PageLoader";
import SmoothScroll from "@/components/SmoothScroll";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index.tsx"));
const TypographyTool = lazy(() => import("./pages/TypographyTool.tsx"));
const AuroraWeaver = lazy(() => import("./pages/AuroraWeaver.tsx"));
const ImageCompressor = lazy(() => import("./pages/ImageCompressor.tsx"));
const ImageCompressorPage = lazy(() => import("./pages/ImageCompressorPage.tsx"));
const BackgroundRemoverPage = lazy(() => import("./pages/BackgroundRemoverPage.tsx"));

const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const SkeletonKb = lazy(() => import("./pages/SkeletonKb"));
const FluidHeroPage = lazy(() => import("./pages/FluidHeroPage.tsx"));
const ChatbotBuilder = lazy(() => import("./pages/ChatbotBuilder"));
const VastuHomePlaner = lazy(() => import("./pages/VastuHomePlaner"));

const ColorPaletteGenerator = lazy(() => import("./pages/ColorPaletteGenerator"));
const PageSpeedPro = lazy(() => import("./pages/PageSpeedPro"));

const ContactUs = lazy(() => import("./pages/ContactUs.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const SeoSearch = lazy(() => import("./pages/SeoSearch.tsx"));
const SocialMedia = lazy(() => import("./pages/SocialMedia.tsx"));
const PerformanceMarketing = lazy(() => import("./pages/PerformanceMarketing.tsx"));
const WebsiteDevelopment = lazy(() => import("./pages/WebsiteDevelopment.tsx"));
const BrandIdentity = lazy(() => import("./pages/BrandIdentity.tsx"));
const VideoMotion = lazy(() => import("./pages/VideoMotion.tsx"));
const InstagramPostingFrequency2026 = lazy(() => import("./pages/blog/InstagramPostingFrequency2026.tsx"));
const AuroraGradientBlogBlue = lazy(() => import("./pages/blog/AuroraGradientBlogBlue.tsx"));
const CheapestWayToBuildWebsiteSmallBusinessIndia = lazy(() => import("./pages/blog/CheapestWayToBuildWebsiteSmallBusinessIndia.tsx"));

const UiDesignInspiration = lazy(() => import("./pages/inspiration/UiDesignInspiration.tsx"));
const WebDesignInspiration = lazy(() => import("./pages/inspiration/WebDesignInspiration.tsx"));
const SaasInspiration = lazy(() => import("./pages/inspiration/SaasInspiration.tsx"));
const LandingPages = lazy(() => import("./pages/inspiration/LandingPages.tsx"));
const MobileAppUi = lazy(() => import("./pages/inspiration/MobileAppUi.tsx"));
const DashboardUi = lazy(() => import("./pages/inspiration/DashboardUi.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <SmoothScroll>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />

                <Route path="/typography" element={<TypographyTool />} />
                <Route path="/aurora" element={<AuroraWeaver />} />
                <Route path="/compressor" element={<ImageCompressor />} />
                <Route path="/internal-kb" element={<SkeletonKb />} />
                <Route path="/chatbot" element={<ChatbotBuilder />} />
                <Route path="/vastu" element={<VastuHomePlaner />} />

                <Route path="/color-palette-generator" element={<ColorPaletteGenerator />} />
                <Route path="/pagespeed" element={<PageSpeedPro />} />

                {/* Wrapped Standalone Tools */}
                <Route path="/image-compressor" element={<ImageCompressorPage />} />
                <Route path="/bg-remover" element={<BackgroundRemoverPage />} />

                {/* Inspiration Routes (SEO Optimized) */}
                <Route path="/inspiration/ui-design" element={<UiDesignInspiration />} />
                <Route path="/inspiration/web-design" element={<WebDesignInspiration />} />
                <Route path="/inspiration/saas" element={<SaasInspiration />} />
                <Route path="/inspiration/landing-pages" element={<LandingPages />} />
                <Route path="/inspiration/mobile-app-ui" element={<MobileAppUi />} />
                <Route path="/inspiration/dashboard-ui" element={<DashboardUi />} />

                {/* Service Pages */}
                <Route path="/services/seo" element={<SeoSearch />} />
                <Route path="/services/social-media" element={<SocialMedia />} />
                <Route path="/services/performance-marketing" element={<PerformanceMarketing />} />
                <Route path="/services/website-development" element={<WebsiteDevelopment />} />
                <Route path="/services/brand-identity" element={<BrandIdentity />} />
                <Route path="/services/video-motion" element={<VideoMotion />} />

                <Route path="/about" element={<About />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/blog/instagram-posting-frequency-2026" element={<InstagramPostingFrequency2026 />} />
                <Route path="/blog/aurora-gradient-blog-blue" element={<AuroraGradientBlogBlue />} />
                <Route path="/blog/cheapest-way-to-build-website-small-business-india" element={<CheapestWayToBuildWebsiteSmallBusinessIndia />} />
                <Route path="/fluid-hero" element={<FluidHeroPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </SmoothScroll>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
